import CarouselDialog from 'components/CarouselDialog/CarouselDialog';
import { useDispatch, useSelector } from 'react-redux';
import { getHasModalOpened, setModalOpened } from 'store/modal';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';

import baby_to_toddler_1 from '../../assets/images/age-group-transition/baby_to_toddler_1.svg';
import baby_to_toddler_2 from '../../assets/images/age-group-transition/baby_to_toddler_2.svg';
import baby_to_toddler_3 from '../../assets/images/age-group-transition/baby_to_toddler_3.svg';
import baby_to_toddler_4 from '../../assets/images/age-group-transition/baby_to_toddler_4.svg';

import toddler_to_child_1 from '../../assets/images/age-group-transition/toddler_to_child_1.svg';
import toddler_to_child_2 from '../../assets/images/age-group-transition/toddler_to_child_2.svg';
import toddler_to_child_3 from '../../assets/images/age-group-transition/toddler_to_child_3.svg';
import toddler_to_child_4 from '../../assets/images/age-group-transition/toddler_to_child_4.svg';

import styles from './AgeGroupTransitionPopup.module.scss';
interface AgeGroupTransitionPopupProps {
  newAgeGroup: 'toddler' | 'child';
}

const AgeGroupTransitionPopup = ({
  newAgeGroup,
}: AgeGroupTransitionPopupProps) => {
  const dispatch = useDispatch();
  const { currentTinyAccount } = useCurrentKitAndTinyAccount();

  const modalIdentifier = {
    name: 'ageGroupTransition',
    props: { newAgeGroup },
  };

  const isPopupDismissed = useSelector(getHasModalOpened(modalIdentifier));

  const isVisible = !isPopupDismissed;

  const dismissPopup = () => {
    dispatch(setModalOpened(modalIdentifier));
  };

  const slides = {
    toddler: [
      {
        imgSrc: baby_to_toddler_1,
        heading:
          'Happy Birthday! At 1 yr old, your child has graduated into a new age bracket.',
        text: "Because they've entered a new age bracket, their microbiome results will look a little different from their previous report.",
        buttonLabel: 'See how',
        isNextButton: true,
      },
      {
        imgSrc: baby_to_toddler_2,
        heading:
          'Toddlers get 20 new metrics that apply to their new age group',
        text: "These new metrics can include digestion-related insights that weren't relevant for babies. For example, fiber digestion and protein breakdown are now measured—two metrics highly shaped by dietary changes during this stage.",
        buttonLabel: 'Next',
        isNextButton: true,
      },
      {
        imgSrc: baby_to_toddler_3,
        heading: 'Optimal levels for bacteria change as children age',
        text: `Metric ranges—low, typical, or high—are calculated using samples from the same age group. A metric classified as "high" for toddlers may not have been considered "high" for babies.  \n Use our over-time comparison tool to review ${currentTinyAccount?.first_name}\'s metrics from previous reports and see how they align with their new age group. Notable changes during this transition include a natural decline in Bifidobacterium.`,
        buttonLabel: 'Next',
        isNextButton: true,
      },
      {
        imgSrc: baby_to_toddler_4,
        heading:
          'Microbiome Summary Score will compare them to their new age group',
        text: 'This score emphasizes the most important metrics for the age group. Baby scores compare babies, and toddler scores compare toddlers. Therefore, it may be hard to track the change between these age groups.',
        buttonLabel: 'Review results',
        buttonAction: () => dismissPopup(),
      },
    ],
    child: [
      {
        imgSrc: toddler_to_child_1,
        heading:
          'Congratulations! Your child graduated from the toddler to child age group',
        text: "Because they've entered a new age bracket, their microbiome results will look a little different from their previous report.",
        buttonLabel: 'See how',
        isNextButton: true,
      },
      {
        imgSrc: toddler_to_child_2,
        heading: 'Children gain 2 new metrics that apply to their age group',
        text: 'These new metrics can include beta-glucuronidase capacity and secondary bile acids.',
        buttonLabel: 'Next',
        isNextButton: true,
      },
      {
        imgSrc: toddler_to_child_3,
        heading: 'Optimal levels for bacteria change as children age',
        text: `Metric ranges—low, typical, or high—are calculated using samples from the same age group. A metric classified as "high" for children may not have been considered "high" for toddlers. \nUse our over-time comparison tool to review ${currentTinyAccount?.first_name}'s metrics from previous reports and see how they align with their new age group. Notable changes during this transition include a natural increase in Faecalibacterium and species richness.`,
        buttonLabel: 'Next',
        isNextButton: true,
      },
      {
        imgSrc: toddler_to_child_4,
        heading:
          'Microbiome Summary Score will compare them to their new age group',
        text: 'This score emphasizes the most important metrics for the age group. Toddler scores compare toddlers, and child scores compare children. Therefore, it may be hard to track the change between these age groups.',
        buttonLabel: 'Review results',
        buttonAction: () => dismissPopup(),
      },
    ],
  };

  const popupColors = {
    toddler: 'Yellow',
    child: 'Orange',
  };

  return (
    <CarouselDialog
      isVisible={isVisible}
      slides={slides[newAgeGroup]}
      popupClassName={styles.container}
      color={popupColors[newAgeGroup]}
      onClose={dismissPopup}
    />
  );
};

export default AgeGroupTransitionPopup;
